/**
 * Record navigation for the workspace: the collapsible sidebar's list and the header pager,
 * driven by one controller.
 *
 * "One controller" is the requirement, not an implementation detail. The sidebar renders
 * `records`; the pager steps over `records`; both are `queue.items`, the same ordered page the
 * active task's queue shows. Nothing here fetches a second list — a pager that disagrees
 * with the visible order is a pager nobody can trust, and there is no way to notice the
 * disagreement from inside the tool.
 *
 * Everything that changes which image is open goes through {@link RecordNavState.go} or
 * {@link RecordNavState.openRecord}, which flush the editing session first. Navigation is the
 * one gesture in this tool that can strand work: `session.close()` writes a localStorage draft
 * on the way out, but a draft is a recovery path, not a save.
 */

import { api } from '../api/client';
import { asProblem, type ApiProblem } from '../api/problem';
import {
  blockedReason,
  describeStep,
  imagePosition,
  recordPosition,
  stepFrom,
  type NavContext,
  type NavDirection,
  type NavEdge,
  type NavStep,
} from '../nav';
import { router } from '../router.svelte';
import { queue } from './queue.svelte';
import { session } from './session.svelte';

const SIDEBAR_KEY = 'hismaic.nav.sidebar';

/**
 * Open by default.
 *
 * The sidebar exists because a round trip through the work queue per inscription is the biggest
 * remaining tax on letters-boxed-per-minute; hiding it until it is discovered would keep paying
 * that tax for whoever never finds the toggle. It collapses to a rail, and the choice persists.
 */
function loadSidebarOpen(): boolean {
  if (typeof localStorage === 'undefined') return true;
  try {
    const raw = localStorage.getItem(SIDEBAR_KEY);
    if (raw === null) return true;
    return raw !== 'closed';
  } catch {
    return true;
  }
}

function storeSidebarOpen(open: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SIDEBAR_KEY, open ? 'open' : 'closed');
  } catch {
    // A browser with storage disabled loses the preference across reloads and nothing else.
  }
}

class RecordNavState {
  sidebarOpen = $state(loadSidebarOpen());
  /** A navigation is resolving (flushing, or fetching the target record's images). */
  busy = $state(false);
  /** Why the last navigation did not happen. Cleared when the next one is attempted. */
  refusal = $state<string | null>(null);
  /** A transport/API failure while looking up the target record. */
  problem = $state<ApiProblem | null>(null);

  /**
   * Everything the pure step functions need, read from the queue and the open session.
   *
   * A single `$derived` rather than a field per value so the sidebar and the pager cannot see
   * two different snapshots of "where am I".
   */
  context = $derived.by<NavContext>(() => {
    const records = queue.items;
    const inscriptionId = session.inscription?.id ?? null;
    const images = session.inscription?.images ?? [];
    const imageId = session.imageId;
    return {
      records,
      recordIndex:
        inscriptionId === null ? -1 : records.findIndex((item) => item.id === inscriptionId),
      images,
      imageIndex: imageId === null ? -1 : images.findIndex((image) => image.id === imageId),
      offset: queue.offset,
      pageSize: queue.pageSize,
      total: queue.total,
      loading: queue.loading,
    };
  });

  get records() {
    return this.context.records;
  }

  get currentInscriptionId(): number | null {
    return session.inscription?.id ?? null;
  }

  get position() {
    return recordPosition(this.context);
  }

  get imagePosition() {
    return imagePosition(this.context);
  }

  step(direction: NavDirection): NavStep | null {
    return stepFrom(this.context, direction);
  }

  /** The button's title: where it goes, or why it cannot. */
  describe(direction: NavDirection): string {
    const step = this.step(direction);
    return step
      ? describeStep(this.context, step, direction)
      : blockedReason(this.context, direction);
  }

  canGo(direction: NavDirection): boolean {
    return !this.busy && this.step(direction) !== null;
  }

  // --- collapse ------------------------------------------------------------------

  toggleSidebar(): void {
    this.setSidebarOpen(!this.sidebarOpen);
  }

  setSidebarOpen(open: boolean): void {
    this.sidebarOpen = open;
    storeSidebarOpen(open);
  }

  // --- navigation ----------------------------------------------------------------

  /**
   * Make sure there is a list to navigate.
   *
   * Deep-linking to `#/image/12` never touches the work queue, so without this the sidebar
   * would be empty and the pager permanently disabled for anyone who bookmarked an image. The
   * same store, so the order matches the queue page exactly.
   *
   * With no task chosen — a deep link, or a record opened from the corpus browser — `load()`
   * is a no-op and the sidebar says so rather than showing an unexplained empty list. Guessing
   * a task to populate it would put a list in front of the annotator that they did not ask
   * for and cannot see the scope of.
   */
  ensureLoaded(): void {
    if (!queue.page && !queue.loading) void queue.load();
  }

  async go(direction: NavDirection): Promise<void> {
    const step = this.step(direction);
    if (!step) {
      this.refusal = blockedReason(this.context, direction);
      return;
    }
    await this.#navigate(() => this.#apply(step));
  }

  /** A click on a sidebar row. Same guard as the pager; the row is not a shortcut around it. */
  async openRecord(inscriptionId: number): Promise<void> {
    if (inscriptionId === this.currentInscriptionId) return;
    await this.#navigate(() => this.#openInscription(inscriptionId, 'first'));
  }

  dismissRefusal(): void {
    this.refusal = null;
    this.problem = null;
  }

  /**
   * The one place unsaved work is protected on the way out.
   *
   * `strandedWork` first: once boxes have failed to reach both the service and localStorage,
   * `session.open()` refuses to load anything over the top of them — but by then the route has
   * already changed and the annotator is looking at an error page instead of their boxes. Better
   * to refuse before moving.
   */
  async #navigate(action: () => Promise<void> | void): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.refusal = null;
    this.problem = null;
    try {
      if (session.strandedWork) {
        this.refusal =
          'You were not moved: this image has boxes that reached neither the service nor this browser’s storage. Copy them out from the save banner first.';
        return;
      }
      if (!(await session.flushForNavigation())) {
        this.refusal =
          'You were not moved: this image has work that is not saved yet. The save banner says why, and the work is still here.';
        return;
      }
      await action();
      // Refresh the shared page so the row the annotator just left shows its new box and image
      // counts. Same fire-and-forget reload `advance()` does after completing an image, and on
      // the same store — the order is by inscription id, so a reload cannot reshuffle the list
      // under the pager, only update the numbers on it.
      if (!this.problem) void queue.load();
    } finally {
      this.busy = false;
    }
  }

  async #apply(step: NavStep): Promise<void> {
    if (step.kind === 'image') {
      router.go({ name: 'image', imageId: step.imageId });
      return;
    }
    if (step.kind === 'record') {
      await this.#openInscription(step.inscriptionId, step.edge);
      return;
    }
    // Off the end of the loaded page. Stepping the *shared* queue offset keeps the sidebar, the
    // pager and the work-queue table on one list; loading a private extra page would not.
    await queue.goTo(step.offset);
    const records = queue.items;
    const target = step.edge === 'first' ? records[0] : records[records.length - 1];
    if (!target) {
      router.go({ name: 'queue', task: queue.taskId });
      return;
    }
    await this.#openInscription(target.id, step.edge);
  }

  /**
   * Open a record at one end of its images.
   *
   * Positional, not "the first unfinished one": the pager is navigation, and stepping back onto
   * a record you finished — to fix a box — has to land you on an image of it rather than
   * skipping past. Complete-and-advance keeps its own dispatch rule (`queue.nextAfter`), which
   * does skip finished work; the two share an order, not a predicate.
   */
  async #openInscription(inscriptionId: number, edge: NavEdge): Promise<void> {
    try {
      const detail = await api.inscriptionById(inscriptionId);
      const image = edge === 'first' ? detail.images[0] : detail.images[detail.images.length - 1];
      if (image) router.go({ name: 'image', imageId: image.id });
      // A record with no imagery cannot be annotated. The inscription view says why, in the
      // catalog's own words, rather than opening an empty canvas.
      else router.go({ name: 'inscription', inscriptionId });
    } catch (error) {
      this.problem = asProblem(error);
    }
  }
}

export const recordNav = new RecordNavState();
